/**
 * Luzie: the LUZIE oil lamp taken apart: the swing-top bottle with its bail, the rubber seal, the CNC-turned wick holder and the flame.
 * Every part is turned, so each is built from its profile ([height, radius]
 * in mm, measured from the CAD) as rings round one vertical axis. At rest the
 * parts float apart along the axis; the part under the pointer pops: it lifts,
 * swells a little and takes the bright edge, the parts above make room and the
 * rest follow, staggered outwards. The slider is how far a part pops, in mm.
 */
const {
  Cam, fillet, fit, hull, open, poly, proj, seg,
  tdone, tset, tval, tween, disposer, mk, pointer, register,
} = HL;

const N = 30;
// bottom to top: name, (unused), pieces (profiles), ribs [dz, r], mark
const PARTS = [["flasche", 0, [[[0, 30], [1.5, 31.5], [101, 31.5], [110, 29], [118, 25], [125, 20], [132, 15.5], [138, 13.7]], [[138, 13.7], [166, 13.9], [167, 14.2]]], [], "bail"], ["dichtring", 0, [[[0, 13.2], [0.5, 14.6], [2, 14.6], [2.5, 13.2]]], [], ""], ["dochthalter", 0, [[[0, 6.6], [2.2, 6.6], [2.4, 5.2], [8, 5.2], [8.4, 5.8], [11, 5.8], [12, 3.2]]], [[5, 5.2]], ""], ["flamme", 0, [[[0, 0], [22, 0]]], [], "flame"]];
const GAP = 14, REST_MARK = 3, S = 0.92, TILT = 0;

// the axis tilts back by TILT degrees: x across, then up and away from the viewer
const T = (TILT * Math.PI) / 180, AXIS = [0, -Math.sin(T), Math.cos(T)], E1 = [1, 0, 0];
const E2 = [0, Math.cos(T), Math.sin(T)], CAM = [0.612, 0.612, 0.5];
const SEE = Math.sign(AXIS[1] * CAM[1] + AXIS[2] * CAM[2]) || 1; // +1: the top faces show, -1: the bottom faces
/** World point at height h along the axis, (u, v) across it. */
const W = (h, u, v) => [E1[0] * u + E2[0] * v + AXIS[0] * h, E1[1] * u + E2[1] * v + AXIS[1] * h, E1[2] * u + E2[2] * v + AXIS[2] * h];
/** A ring of N samples round the axis at height h; with keep, only the run that faces the viewer, in order. */
const ring = (P, h, r, keep) => {
  const pt = [], vis = [];
  for (let i = 0; i < N; i++) {
    const t = (i / N) * Math.PI * 2, c = Math.cos(t), s = Math.sin(t);
    pt.push(P(...W(h, r * c, r * s)));
    vis.push((E1[0] * c + E2[0] * s) * CAM[0] + (E1[1] * c + E2[1] * s) * CAM[1] + (E1[2] * c + E2[2] * s) * CAM[2] > 0);
  }
  if (!keep) return pt;
  let st = vis.findIndex((v, i) => v && !vis[(i + N - 1) % N]);
  if (st < 0) st = 0;
  const out = [];
  for (let j = 0; j <= N; j++) { const i = (st + j) % N; if (!vis[i]) break; out.push(pt[i]); }
  return out;
};

/** One turned piece from z0, swollen by k: its silhouette and the crease round its visible end face. */
function piece(P, prof, z0, k) {
  let pts = [];
  for (const [h, r] of prof) pts = pts.concat(ring(P, z0 + h, Math.max(r * k, 0.3)));
  const [h, r] = SEE > 0 ? prof[prof.length - 1] : prof[0];
  const crease = r > 2 ? poly(ring(P, z0 + h, r * k - Math.min(1.2, r * 0.2))) : "";
  return [poly(hull(pts)), crease];
}

/** A part's marks: ribs round its body, and what only this part has. */
function marks(P, part, z0, k) {
  const [, , pieces, ribs, mark] = part, prof = pieces[0];
  let d = "";
  for (const [h, r] of ribs) d += open(ring(P, z0 + h, r * k, true));
  if (mark === "chip") { // the LED chip: a small square die on the face that looks at the lens
    const h = SEE > 0 ? prof[prof.length - 1][0] + 0.6 : -0.6, c = 2.8;
    d += poly([[-c, -c], [c, -c], [c, c], [-c, c]].map(([u, v]) => P(...W(z0 + h, u, v))));
  }
  if (mark === "flutes") { // milled flutes along the housing, on the side that faces us
    for (let i = 0; i < 7; i++) {
      const t = (Math.PI * 2 * i) / 7 + 0.3, r = prof[1][1] * k, n = [Math.cos(t), Math.sin(t)];
      const nv = [E1[0] * n[0] + E2[0] * n[1], E1[1] * n[0] + E2[1] * n[1], E1[2] * n[0] + E2[2] * n[1]];
      if (nv[0] * CAM[0] + nv[1] * CAM[1] + nv[2] * CAM[2] < 0.25) continue;
      d += seg(P(...W(z0 + 6, r * n[0], r * n[1])), P(...W(z0 + prof[2][0] - 4, r * n[0], r * n[1])));
    }
  }
  if (mark === "bail") { // the swing-top bail, hung down along the shoulder on both sides
    const q = Math.SQRT1_2; // across the screen, so both wires show beside the neck
    for (const s of [-1, 1]) d += open([[15, 151], [24, 148], [29, 139], [32, 126], [34, 113], [30, 109]].map(([y, z]) => P(...W(z0 + z, s * y * q, -s * y * q))));
  }
  return d;
}

/** The flame on top: a drop standing on its base point, in screen space. */
function flame(P, z0, k) {
  const [x, y] = P(...W(z0, 0, 0)), h = 22 * k * S * 0.87, w = h * 0.3;
  return poly(fillet([[x, y - h], [x + w, y - h * 0.36], [x + w * 0.55, y], [x - w * 0.55, y], [x - w, y - h * 0.36]], [1, w * 0.9, w * 0.5, w * 0.5, w * 0.9], 5));
}

function mount({ stage, svg, read }, value) {
  const bag = disposer();
  let pop = value;
  const top = (i) => Math.max(...PARTS[i][2].map((q) => q[q.length - 1][0]));
  // the parts stand one above the other, each a gap above the last one's top
  const REST = [];
  PARTS.forEach((_, i) => REST.push(i ? REST[i - 1] + top(i - 1) + GAP : 0));
  const R = Math.max(...PARTS.flatMap((p) => p[2].flat().map((q) => q[1])));

  const C = Cam(45, 0.5, S);
  const zMax = REST[REST.length - 1] + top(PARTS.length - 1) + 30;
  const ext = [];
  for (const h of [REST[0] - 2, zMax]) for (const [u, v] of [[R, 0], [-R, 0], [0, R], [0, -R]]) ext.push(W(h, u, v));
  fit(C, ext, 200, 166);
  const P = proj(C);
  const g = mk("g", {}, svg);
  mk("path", { d: seg(P(...W(REST[0] - 4, 0, 0)), P(...W(zMax - 26, 0, 0))), class: "dash" }, g);

  // paint far to near: bottom up when the top faces show, top down when the bottom faces do (rule 06)
  const order = PARTS.map((_, i) => i);
  if (SEE < 0) order.reverse();
  const groups = [];
  order.forEach((i) => { groups[i] = mk("g", {}, g); });
  const parts = PARTS.map((p, i) => {
    const grp = groups[i];
    // inside a part too, the far pieces go down first
    const sols = p[2].map(() => null), idx = p[2].map((_, j) => j);
    (SEE < 0 ? idx.reverse() : idx).forEach((j) => { sols[j] = [mk("path", { class: "sil" }, grp), mk("path", { class: "nf lo" }, grp)]; });
    const extra = mk("path", { class: p[4] === "flame" || p[4] === "bail" ? "nf" : "nf lo" }, grp);
    return { p, sols, extra, z: tween(0), k: tween(1), last: null };
  });

  function draw(i, lift, k) {
    const pt = parts[i], key = lift.toFixed(2) + k.toFixed(3);
    if (pt.last === key) return;
    pt.last = key;
    const z0 = REST[i] + lift;
    if (pt.p[4] === "flame") { pt.extra.setAttribute("d", flame(P, z0, k)); return; }
    pt.p[2].forEach((prof, j) => { const [s, c] = piece(P, prof, z0, k); pt.sols[j][0].setAttribute("d", s); pt.sols[j][1].setAttribute("d", c); });
    pt.extra.setAttribute("d", marks(P, pt.p, z0, k));
  }

  const B = register(stage, (_dt, now) => {
    let moving = false;
    parts.forEach((pt, i) => { draw(i, tval(pt.z, now), tval(pt.k, now)); if (!tdone(pt.z, now) || !tdone(pt.k, now)) moving = true; });
    return moving;
  });
  bag.add(B.unregister);

  // hit: each part's centre in its REST pose, which never moves (rule 01)
  const hits = PARTS.map((p, i) => P(...W(REST[i] + (p[4] === "flame" ? 10 : top(i) / 2), 0, 0)));
  function hit([x, y]) {
    let a = -1, bd = R * S * 1.3;
    hits.forEach((h, i) => { const d = Math.hypot(h[0] - x, h[1] - y); if (d < bd) { bd = d; a = i; } });
    return a;
  }

  let act = -2;
  function setActive(a) {
    if (a === act) return;
    const now = performance.now(), from = a >= 0 ? a : Math.max(act, 0);
    act = a;
    parts.forEach((pt, i) => {
      const lift = a < 0 ? 0 : i > a ? pop : i === a ? pop * 0.55 : 0;
      tset(pt.z, lift, now, Math.abs(i - from) * 40);
      tset(pt.k, i === a ? 1.07 : 1, now, Math.abs(i - from) * 40);
      const on = a < 0 ? i === REST_MARK : i === a;
      pt.sols.forEach(([s]) => s.classList.toggle("hi", on));
      if (pt.p[4] === "flame") pt.extra.classList.toggle("hi", on);
    });
    read.textContent = a < 0 ? "rest" : PARTS[a][0];
    B.wake();
  }

  setActive(-1);
  bag.add(pointer(stage, { move: (p) => setActive(hit(p)), leave: () => setActive(-1) }));
  bag.add(() => svg.replaceChildren());
  return {
    set: (v) => { pop = v; const a = act; act = -2; setActive(a); },
    destroy: bag.dispose,
  };
}

hairline({
  name: "luzie",
  means: "The LUZIE oil lamp in its parts: bottle, seal, turned wick holder and flame; the part under the pointer pops up.",
  rules: [1, 2, 4, 9],
  range: [8, 16, 26],
  tour: [[200, 215], [200, 100], [200, 62], null],
  mount,
});
